import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BILLING_STATUS, BILLING_URLS, BillingStatusType } from '../../config/Constants';
import { getBillingStatus, formatBillingDate, BillingInfo, isBillingEnabled } from '../../services/billingService';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import api from '../../config/axiosConfig';
import { sessionManager } from '../../config/sessionManager';

const Billing: React.FC = () => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingAutoRenewal, setProcessingAutoRenewal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Check if user has access to billing page (Head Coach or Assistant Coach only)
  const hasAccess = sessionManager.isHeadCoach() || sessionManager.isAssistantCoach();

  useEffect(() => {
    // Redirect if user doesn't have access
    if (!hasAccess) {
      showErrorMsg('You do not have permission to access the billing page');
      navigate('/');
    }
  }, [hasAccess, navigate]);

  const loadBillingStatus = useCallback(async () => {
    if (!hasAccess) return;

    try {
      setLoading(true);
      const response = await getBillingStatus();
      if (response.success && response.billing) {
        setBilling(response.billing);
      } else {
        showErrorMsg(response.message || 'Failed to load billing information');
      }
    } catch (error) {
      console.error('Error loading billing status:', error);
      showErrorMsg('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    loadBillingStatus();
  }, [loadBillingStatus]);

  useEffect(() => {
    if (billing && !isBillingEnabled(billing)) {
      showErrorMsg('Billing is disabled in this environment.');
      navigate('/');
    }
  }, [billing, navigate]);

  // If no access, don't render anything (will redirect)
  if (!hasAccess || (billing && !isBillingEnabled(billing))) {
    return null;
  }

  // Get status display info
  /** Free games are counted in billing only for trial + pending — match backend canAddGame / incrementGameCount */
  const showFreeGameTracking = (status: BillingStatusType) =>
    status === BILLING_STATUS.TRIAL || status === BILLING_STATUS.PENDING;

  const getStatusInfo = (status: BillingStatusType) => {
    const statusMap = {
      [BILLING_STATUS.TRIAL]: {
        text: 'Free Games/Scrimmages',
        bgColor: 'bg-amber-100',
        textColor: 'text-amber-800',
        borderColor: 'border-amber-200',
        iconBg: 'bg-amber-500',
      },
      [BILLING_STATUS.INVOICE_DRAFT]: {
        text: 'Invoice Preparing',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        borderColor: 'border-purple-200',
        iconBg: 'bg-purple-500',
      },
      [BILLING_STATUS.PENDING]: {
        text: 'Invoice Sent',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200',
        iconBg: 'bg-blue-500',
      },
      [BILLING_STATUS.ACTIVE]: {
        text: 'Active',
        bgColor: 'bg-emerald-100',
        textColor: 'text-emerald-800',
        borderColor: 'border-emerald-200',
        iconBg: 'bg-emerald-500',
      },
      [BILLING_STATUS.INVOICE_SENT]: {
        text: 'Renewal Invoice Sent',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200',
        iconBg: 'bg-blue-500',
      },
      [BILLING_STATUS.PAST_DUE]: {
        text: 'Past Due',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
        borderColor: 'border-orange-200',
        iconBg: 'bg-orange-500',
      },
      [BILLING_STATUS.SUSPENDED]: {
        text: 'Suspended',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200',
        iconBg: 'bg-red-500',
      },
      [BILLING_STATUS.CANCELED]: {
        text: 'Canceled',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200',
        iconBg: 'bg-gray-500',
      },
    };
    return statusMap[status] || statusMap[BILLING_STATUS.CANCELED];
  };

  // Status icons
  const StatusIcon = ({ status }: { status: BillingStatusType }) => {
    switch (status) {
      case BILLING_STATUS.TRIAL:
        return (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case BILLING_STATUS.INVOICE_DRAFT:
        return (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case BILLING_STATUS.PENDING:
        return (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        );
      case BILLING_STATUS.ACTIVE:
        return (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case BILLING_STATUS.INVOICE_SENT:
        return (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        );
      case BILLING_STATUS.PAST_DUE:
        return (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        );
      case BILLING_STATUS.SUSPENDED:
        return (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
    }
  };

  /** Subscribe card: create send-invoice subscription, stay on page; Stripe emails the invoice */
  const handleSubscribePayByInvoice = async () => {
    try {
      setProcessingPayment(true);
      const response = await api.post(BILLING_URLS.CREATE_CHECKOUT_SESSION);

      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
        return;
      }
      if (response.data.invoiceUrl) {
        showSuccessMsg(
          'Your subscription invoice is set up. Stripe will email you a link to view and pay. Use Open invoice to pay on this page if you need the link now.',
        );
        await loadBillingStatus();
        return;
      }
      showErrorMsg(response.data.message || 'Failed to start invoice billing');
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      showErrorMsg(error.response?.data?.message || 'Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  /** Status card: fetch hosted invoice URL and open it (new tab so this billing page stays open) */
  const handleOpenInvoiceToPay = async () => {
    try {
      setProcessingPayment(true);
      const response = await api.post(BILLING_URLS.CREATE_CHECKOUT_SESSION);

      if (response.data.invoiceUrl) {
        window.open(response.data.invoiceUrl, '_blank', 'noopener,noreferrer');
        return;
      }
    } catch (error: any) {
      console.error('Error loading invoice URL:', error);
      showErrorMsg(error.response?.data?.message || 'Failed to open invoice');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Handle auto-renewal - redirect to Stripe Checkout
  const handleAutoRenewal = async () => {
    try {
      setProcessingAutoRenewal(true);
      const response = await api.post(BILLING_URLS.AUTO_RENEWAL);
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        showErrorMsg(response.data.message || 'Failed to start auto-renewal setup');
      }
    } catch (error: any) {
      console.error('Error creating auto-renewal session:', error);
      showErrorMsg(error.response?.data?.message || 'Failed to start auto-renewal setup');
    } finally {
      setProcessingAutoRenewal(false);
    }
  };

  // Handle cancel subscription at end of period
  const handleCancelSubscription = async () => {
    try {
      setCancelling(true);
      const response = await api.post(BILLING_URLS.CANCEL);
      if (response.data.success) {
        showSuccessMsg('Subscription will be cancelled at end of billing period');
        setShowCancelConfirm(false);
        await loadBillingStatus();
      } else {
        showErrorMsg(response.data.message || 'Failed to cancel subscription');
      }
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      showErrorMsg(error.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="relative">
          <div
            className="rounded-full h-16 w-16 border-4 border-transparent border-r-emerald-400 animate-spin"
            style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}
          />
        </div>
      </div>
    );
  }

  // No billing info
  if (!billing) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-emerald-50">
        <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <h1 className="text-xl font-bold">Billing and Renewals</h1>
                <p className="text-green-100 text-base mt-1">Manage your subscription and payment details</p>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white max-w-5xl mx-auto shadow-xl rounded-2xl p-8 border border-gray-100 text-center">
            <p className="text-gray-600">No billing information available.</p>
            <p className="text-sm text-gray-500 mt-2">
              Contact{' '}
              <a href="mailto:support@statdog.live" className="text-green-600 hover:underline">
                support@statdog.live
              </a>{' '}
              for assistance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(billing.status);
  const paymentMethodDisplay =
    billing.paymentMethodDisplay ||
    (billing.payMethod === 'autoRenew' ? 'Auto Renewal' : billing.payMethod === 'invoice' ? 'Pay by Invoice' : 'Not Set');
  const isAutoRenewal = billing.isAutoRenewal ?? billing.payMethod === 'autoRenew';
  const isTrial = billing.status === BILLING_STATUS.TRIAL;
  const hasOpenInvoice = (
    [BILLING_STATUS.PENDING, BILLING_STATUS.INVOICE_SENT, BILLING_STATUS.PAST_DUE] as BillingStatusType[]
  ).includes(billing.status);
  const hasSubscription = (
    [
      BILLING_STATUS.ACTIVE,
      BILLING_STATUS.PENDING,
      BILLING_STATUS.INVOICE_SENT,
      BILLING_STATUS.INVOICE_DRAFT,
      BILLING_STATUS.PAST_DUE,
    ] as BillingStatusType[]
  ).includes(billing.status);
  const isCancelPending = billing.cancelAtPeriodEnd === true;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h1 className="text-xl font-bold">Billing and Renewals</h1>
              <p className="text-green-100 text-base mt-1">Manage your subscription and payment details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Status Card */}
          <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-12 h-12 ${statusInfo.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}
                  >
                    <StatusIcon status={billing.status} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Subscription Status</h2>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.textColor} mt-1`}
                    >
                      {isCancelPending ? 'Cancelling at Period End' : statusInfo.text}
                    </span>
                  </div>
                </div>
                {/* Action buttons in status card header */}
                <div className="flex flex-wrap gap-3">
                  {/* Open Stripe hosted invoice (PENDING, INVOICE_SENT, PAST_DUE) */}
                  {hasOpenInvoice && (
                    <button
                      type="button"
                      onClick={handleOpenInvoiceToPay}
                      disabled={processingPayment}
                      className="flex items-center justify-center space-x-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-md"
                    >
                      {processingPayment ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          <span>Loading...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                          <span>Open invoice to pay</span>
                        </>
                      )}
                    </button>
                  )}
                  {/* Cancel: shown when subscription is active and not already pending cancellation */}
                  {hasSubscription && !isCancelPending && (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      disabled={cancelling}
                      className="flex items-center justify-center space-x-2 px-5 py-2.5 bg-white text-red-600 border-2 border-red-200 rounded-xl hover:bg-red-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Cancel Subscription</span>
                    </button>
                  )}
                </div>
              </div>
              {/* Cancellation pending notice inside status card */}
              {isCancelPending && billing.currentPeriodEnd && (
                <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <svg
                    className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-sm text-amber-800">
                    Your subscription will end on <strong>{formatBillingDate(billing.currentPeriodEnd)}</strong>. Full
                    access continues until then. To resume, contact{' '}
                    <a href="mailto:support@statdog.live" className="underline font-medium">
                      support@statdog.live
                    </a>
                    .
                  </p>
                </div>
              )}
            </div>

            {/* Status Message */}
            <div className="p-6 bg-gradient-to-r from-gray-50 to-white">
              <p className="text-gray-600">
                {billing.status === BILLING_STATUS.TRIAL && (
                  <>
                    Add Your <span className="font-semibold">Players</span> &gt; Create Your{' '}
                    <span className="font-semibold">Season</span> &gt; Add <span className="font-semibold">Games</span> &gt; Invite{' '}
                    <span className="font-semibold">Users</span> – stat takers &amp; coaches
                    <br />
                    You have{' '}
                    <span className="font-semibold text-amber-600">
                      {billing.freeGamesRemaining || 0} free games
                    </span>{' '}
                    remaining. Start tracking stats today!
                  </>
                )}
                {billing.status === BILLING_STATUS.INVOICE_DRAFT && (
                  <>
                    Your invoice is being prepared and will be sent to your email shortly. You can add games and track
                    stats while it is being finalized.
                  </>
                )}
                {billing.status === BILLING_STATUS.PENDING && (
                  <>
                    Your invoice has been sent to your email. Please complete payment to continue using STATDOG after
                    your free games are used. You have{' '}
                    <span className="font-semibold text-blue-700">
                      {billing.freeGamesRemaining || 0} free game
                      {(billing.freeGamesRemaining || 0) === 1 ? '' : 's'}
                    </span>{' '}
                    remaining.
                  </>
                )}
                {billing.status === BILLING_STATUS.ACTIVE && (
                  <>
                    {billing.currentPeriodEnd && new Date(billing.currentPeriodEnd) < new Date() ? (
                      <>
                        Your renewal invoice is being prepared and will be sent to your email shortly. Your access
                        remains fully active.
                      </>
                    ) : (
                      <>
                        Your subscription is active.{' '}
                        {isAutoRenewal ? 'You are enrolled in Auto Renewal.' : ''}
                        {!isAutoRenewal ? ' Thank you for being a STATDOG customer!' : ''}
                      </>
                    )}
                  </>
                )}
                {billing.status === BILLING_STATUS.INVOICE_SENT && (
                  <>
                    Your renewal invoice has been sent. Please complete payment within the due date to keep your
                    subscription active.
                  </>
                )}
                {billing.status === BILLING_STATUS.PAST_DUE && (
                  <>Your payment is past due. Please pay to avoid service interruption.</>
                )}
                {billing.status === BILLING_STATUS.SUSPENDED && (
                  <>Your account is suspended due to non-payment. Contact support to restore access.</>
                )}
                {billing.status === BILLING_STATUS.CANCELED && <>Your subscription has been canceled.</>}
              </p>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Subscription Details */}
            <div
              className={`bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 ${
                !showFreeGameTracking(billing.status) ? 'md:col-span-2' : ''
              }`}
            >
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 border-b border-green-100">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Subscription Details</h3>
                  </div>
                  <a
                    href="https://statdog.live/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-green-700 hover:text-green-800 underline"
                  >
                    Pricing
                  </a>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <span>Plan</span>
                  </span>
                  <span className="font-medium text-gray-900">
                    {showFreeGameTracking(billing.status) ? 'Trial' : 'STATDOG - Annual'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2m2-8h.01M19 12h.01M19 16h.01"
                      />
                    </svg>
                    <span>Payment Method</span>
                  </span>
                  <span className={`font-medium ${isAutoRenewal ? 'text-blue-700' : 'text-gray-900'}`}>
                    {paymentMethodDisplay}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>Status</span>
                  </span>
                  <span className={`font-medium ${statusInfo.textColor}`}>{statusInfo.text}</span>
                </div>
                {billing.status === BILLING_STATUS.ACTIVE && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600 flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span>Next Billing</span>
                    </span>
                    <span className="font-medium text-gray-900">
                      {billing.currentPeriodEnd ? formatBillingDate(billing.currentPeriodEnd) : '-'}
                    </span>
                  </div>
                )}
                {billing.lastPaymentAt && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>Last Payment</span>
                    </span>
                    <span className="font-medium text-gray-900">{formatBillingDate(billing.lastPaymentAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Game Usage — only while free games are counted (trial + pending) */}
            {showFreeGameTracking(billing.status) && (
              <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 border-b border-amber-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Free trial games</h3>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Free games/scrimmages added.</span>
                      <span className="font-medium text-gray-900">
                        {billing.freeGamesUsed || 0} / {billing.freeGamesLimit || 3}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-3 rounded-full transition-all duration-500 bg-emerald-500"
                        style={{
                          width: `${Math.min(
                            ((billing.freeGamesUsed || 0) / (billing.freeGamesLimit || 3)) * 100,
                            100,
                          )}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600 flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      <span>Games remaining</span>
                    </span>
                    <span className={`font-medium text-emerald-600`}>{billing.freeGamesRemaining || 0}</span>
                  </div>
                  {billing.trialStartedAt && (
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600 flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span>Started</span>
                      </span>
                      <span className="font-medium text-gray-900">{formatBillingDate(billing.trialStartedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Alert Cards */}
          {billing.status === BILLING_STATUS.PAST_DUE && billing.graceDeadline && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-orange-800 text-lg">Payment Required</h4>
                  <p className="text-orange-700 mt-1">
                    Your payment is past due. Please pay by <strong>{formatBillingDate(billing.graceDeadline)}</strong>{' '}
                    to avoid account suspension.
                  </p>
                </div>
              </div>
            </div>
          )}

          {billing.status === BILLING_STATUS.SUSPENDED && (
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-red-800 text-lg">Account Suspended</h4>
                  <p className="text-red-700 mt-1">
                    Your account has been suspended due to non-payment. Please contact{' '}
                    <a href="mailto:support@statdog.live" className="underline font-medium hover:text-red-900">
                      support@statdog.live
                    </a>{' '}
                    to restore your access.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Options — only shown for TRIAL (choose how to subscribe) */}
          {isTrial && !isCancelPending && (
            <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-blue-100">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Subscribe to STATDOG</h3>
                  </div>
                  <a
                    href="https://statdog.live"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-700 hover:text-blue-800 underline"
                  >
                    Learn more about STATDOG
                  </a>
                </div>
              </div>
              <div className="p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Pay by Invoice */}
                  <button
                    type="button"
                    onClick={handleSubscribePayByInvoice}
                    disabled={processingPayment || processingAutoRenewal}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg shadow-green-600/20"
                  >
                    {processingPayment ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <span>Pay by Invoice</span>
                      </>
                    )}
                  </button>

                  {/* Auto-Renewal */}
                  <button
                    onClick={handleAutoRenewal}
                    disabled={processingPayment || processingAutoRenewal}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg shadow-blue-600/20"
                  >
                    {processingAutoRenewal ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>Redirecting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                          />
                        </svg>
                        <span>Auto-Renewal</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  <strong>Pay by Invoice</strong> creates your annual invoice (Net-30) to provide to your school or
                  boosters to pay by credit card, ACH, or check. Stripe emails you a payment link; after your status
                  shows Invoice Sent, use <strong>Open invoice to pay</strong> above anytime.{' '}
                  <strong>Auto-Renewal</strong> allows you to conveniently have your card charged each year.
                </p>
              </div>
            </div>
          )}

          {/* Cancel Confirmation Dialog */}
          {showCancelConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancel Subscription?</h3>
                <p className="text-gray-600 mb-6">
                  Your subscription will remain active until the end of your current billing period
                  {billing.currentPeriodEnd ? ` (${formatBillingDate(billing.currentPeriodEnd)})` : ''}. After that, you
                  will lose access to STATDOG.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    disabled={cancelling}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Keep Subscription
                  </button>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Contact Support */}
          <div className="text-center py-4">
            <p className="text-gray-600">
              Questions about billing?{' '}
              <a
                href="mailto:support@statdog.live"
                className="text-green-600 hover:text-green-700 font-medium hover:underline"
              >
                Contact support@statdog.live
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
